import React, {useEffect, useMemo, useState} from 'react';
import {Avatar, Button, Input, Tag, message} from 'antd';
import {UserOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, CaretUpOutlined, CaretDownOutlined, RobotOutlined} from '@ant-design/icons';
import {useAuth} from '../context/auth-context';
import {ROLE_RANK} from '../utils/role-rank';
import './comments-panel.scss';

const ROLE_EDITOR = ROLE_RANK.Editor;

// Field-anchored discussion thread for one (spec_path, json_pointer).
// Renders a flat list as a tree using parent_id; new comments / edits /
// deletes / resolves apply optimistically and reconcile against the
// server's response. Failed mutations roll back and surface a toast.
//
// Day 11 will lift this into the editor's right-pane slot via the
// rightPanelSlots prop on <TenetEditor>; today the dashboard mounts it
// directly for visual + interaction testing.

function CommentsPanel({spec_path, json_pointer}) {
    const {user} = useAuth();
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [composeBody, setComposeBody] = useState('');
    const [posting, setPosting] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [replyBody, setReplyBody] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editBody, setEditBody] = useState('');

    const isEditor = user && ROLE_RANK[user.role] >= ROLE_EDITOR;

    useEffect(() => {
        if (!spec_path) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({spec_path});
        if (json_pointer) params.set('json_pointer', json_pointer);
        fetch(`/api/comments?${params.toString()}`)
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((d) => {
                if (!cancelled) setComments(d.comments || []);
            })
            .catch((err) => {
                if (!cancelled) setError(err.message);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [spec_path, json_pointer]);

    const tree = useMemo(() => threadComments(comments), [comments]);

    async function postComment(body, parent_id) {
        if (!body.trim()) return;
        const tmpId = `tmp-${Date.now()}`;
        const optimistic = optimisticRow(tmpId, body, parent_id, spec_path, json_pointer, user);
        setComments((prev) => [...prev, optimistic]);
        try {
            const r = await fetch('/api/comments', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({spec_path, json_pointer, body, parent_id: parent_id || null}),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json();
            setComments((prev) => prev.map((c) => (c.id === tmpId ? d.comment : c)));
        } catch (err) {
            setComments((prev) => prev.filter((c) => c.id !== tmpId));
            message.error('Failed to post comment');
        }
    }

    async function editComment(id, body) {
        if (!body.trim()) return;
        const before = comments.find((c) => c.id === id);
        setComments((prev) =>
            prev.map((c) => (c.id === id ? {...c, body, edited_at: new Date().toISOString()} : c)),
        );
        try {
            const r = await fetch(`/api/comments/${id}`, {
                method: 'PATCH',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({body}),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json();
            setComments((prev) => prev.map((c) => (c.id === id ? d.comment : c)));
        } catch (err) {
            setComments((prev) => prev.map((c) => (c.id === id ? before : c)));
            message.error('Failed to update comment');
        }
    }

    async function deleteComment(id) {
        const snapshot = comments;
        setComments((prev) => prev.filter((c) => c.id !== id && c.parent_id !== id));
        try {
            const r = await fetch(`/api/comments/${id}`, {method: 'DELETE'});
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
        } catch (err) {
            setComments(snapshot);
            message.error('Failed to delete comment');
        }
    }

    async function resolveComment(id) {
        const before = comments.find((c) => c.id === id);
        const now = new Date().toISOString();
        setComments((prev) =>
            prev.map((c) => (c.id === id ? {...c, resolved_at: now, resolved_by: user.id} : c)),
        );
        try {
            const r = await fetch(`/api/comments/${id}/resolve`, {method: 'POST'});
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json();
            setComments((prev) => prev.map((c) => (c.id === id ? d.comment : c)));
        } catch (err) {
            setComments((prev) => prev.map((c) => (c.id === id ? before : c)));
            message.error('Failed to resolve comment');
        }
    }

    async function voteComment(id, direction) {
        const before = comments.find((c) => c.id === id);
        if (!before || (before._optimistic && String(before.id).startsWith('tmp-'))) return;
        const oldVote = before.my_vote || 0;
        const newVote = oldVote === direction ? null : direction;
        const delta = (newVote || 0) - oldVote;
        setComments((prev) =>
            prev.map((c) => (c.id === id ? {...c, my_vote: newVote, score: (c.score || 0) + delta} : c)),
        );
        try {
            const url = `/api/comments/${id}/vote`;
            const r = newVote === null
                ? await fetch(url, {method: 'DELETE'})
                : await fetch(url, {
                    method: 'PUT',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({value: newVote}),
                });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json();
            setComments((prev) => prev.map((c) => (c.id === id ? d.comment : c)));
        } catch (err) {
            setComments((prev) => prev.map((c) => (c.id === id ? before : c)));
            message.error('Failed to record vote');
        }
    }

    return (
        <div className="portal-comments">
            <div className="portal-comments__header">
                <h3>Discussion</h3>
                <p className="portal-comments__anchor">
                    {spec_path}{json_pointer ? <span className="portal-comments__pointer"> · {json_pointer}</span> : null}
                </p>
            </div>

            <form
                className="portal-comments__compose"
                onSubmit={async (e) => {
                    e.preventDefault();
                    setPosting(true);
                    await postComment(composeBody, null);
                    setComposeBody('');
                    setPosting(false);
                }}
            >
                <Input.TextArea
                    rows={3}
                    placeholder="Start a thread…"
                    value={composeBody}
                    onChange={(e) => setComposeBody(e.target.value)}
                />
                <Button type="primary" htmlType="submit" loading={posting} disabled={!composeBody.trim()}>
                    Post
                </Button>
            </form>

            {error ? <div className="portal-comments__error">Error: {error}</div> : null}
            {loading ? <div className="portal-comments__loading">Loading…</div> : null}
            {!loading && tree.length === 0 ? <div className="portal-comments__empty">No comments yet.</div> : null}

            <ul className="portal-comments__list">
                {tree.map((c) => (
                    <CommentItem
                        key={c.id}
                        comment={c}
                        depth={0}
                        currentUser={user}
                        isEditor={isEditor}
                        replyTo={replyTo}
                        setReplyTo={setReplyTo}
                        replyBody={replyBody}
                        setReplyBody={setReplyBody}
                        editingId={editingId}
                        setEditingId={setEditingId}
                        editBody={editBody}
                        setEditBody={setEditBody}
                        onPostReply={postComment}
                        onEdit={editComment}
                        onDelete={deleteComment}
                        onResolve={resolveComment}
                        onVote={voteComment}
                    />
                ))}
            </ul>
        </div>
    );
}

function CommentItem(props) {
    const {comment, depth, currentUser, isEditor} = props;
    const {replyTo, setReplyTo, replyBody, setReplyBody} = props;
    const {editingId, setEditingId, editBody, setEditBody} = props;
    const {onPostReply, onEdit, onDelete, onResolve, onVote} = props;

    const isOwner = currentUser && comment.user_id === currentUser.id;
    const isAdmin = currentUser && currentUser.role === 'Administrator';
    const canModify = isOwner || isAdmin;
    const canResolve = isEditor && depth === 0 && !comment.resolved_at;
    const isAi = comment.source === 'ai_proposal';
    const isReplyOpen = replyTo === comment.id;
    const isEditOpen = editingId === comment.id;

    const isTmp = String(comment.id).startsWith('tmp-');
    const score = comment.score || 0;
    const myVote = comment.my_vote || 0;

    return (
        <li className={'portal-comments__item' + (comment.resolved_at ? ' portal-comments__item--resolved' : '')}>
            <div className="portal-comments__vote">
                <button
                    type="button"
                    className={'portal-comments__vote-btn' + (myVote === 1 ? ' portal-comments__vote-btn--active' : '')}
                    onClick={() => onVote(comment.id, 1)}
                    disabled={isTmp}
                    aria-label="Upvote"
                >
                    <CaretUpOutlined />
                </button>
                <span className={'portal-comments__score' + (score > 0 ? ' portal-comments__score--pos' : score < 0 ? ' portal-comments__score--neg' : '')}>
                    {score}
                </span>
                <button
                    type="button"
                    className={'portal-comments__vote-btn' + (myVote === -1 ? ' portal-comments__vote-btn--active-down' : '')}
                    onClick={() => onVote(comment.id, -1)}
                    disabled={isTmp}
                    aria-label="Downvote"
                >
                    <CaretDownOutlined />
                </button>
            </div>
            <Avatar
                size={32}
                src={comment.author_avatar_url || undefined}
                icon={!comment.author_avatar_url ? <UserOutlined /> : undefined}
            />
            <div className="portal-comments__body">
                <div className="portal-comments__meta">
                    <span className="portal-comments__author">{comment.author_display_name || comment.author_email}</span>
                    {isAi ? <span className="portal-comments__ai-badge"><RobotOutlined /> AI proposal</span> : null}
                    {comment.resolved_at ? <Tag color="green" icon={<CheckCircleOutlined />}>Resolved</Tag> : null}
                    <span className="portal-comments__timestamp">{formatDate(comment.created_at)}</span>
                    {comment.edited_at ? <span className="portal-comments__edited">(edited)</span> : null}
                </div>

                {isEditOpen ? (
                    <form
                        className="portal-comments__edit"
                        onSubmit={async (e) => {
                            e.preventDefault();
                            await onEdit(comment.id, editBody);
                            setEditingId(null);
                            setEditBody('');
                        }}
                    >
                        <Input.TextArea
                            rows={2}
                            value={editBody}
                            onChange={(e) => setEditBody(e.target.value)}
                            autoFocus
                        />
                        <div className="portal-comments__edit-actions">
                            <Button size="small" type="primary" htmlType="submit" disabled={!editBody.trim()}>Save</Button>
                            <Button size="small" onClick={() => { setEditingId(null); setEditBody(''); }}>Cancel</Button>
                        </div>
                    </form>
                ) : (
                    <p className="portal-comments__text">{comment.body}</p>
                )}

                {isAi && comment.anchor_diff ? (
                    <pre className="portal-comments__diff">{JSON.stringify(comment.anchor_diff, null, 2)}</pre>
                ) : null}

                <div className="portal-comments__actions">
                    <Button size="small" type="link" onClick={() => setReplyTo(isReplyOpen ? null : comment.id)}>
                        {isReplyOpen ? 'Cancel reply' : 'Reply'}
                    </Button>
                    {canModify && !isEditOpen ? (
                        <Button
                            size="small"
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => { setEditingId(comment.id); setEditBody(comment.body); }}
                        >
                            Edit
                        </Button>
                    ) : null}
                    {canModify ? (
                        <Button size="small" type="link" danger icon={<DeleteOutlined />} onClick={() => onDelete(comment.id)}>
                            Delete
                        </Button>
                    ) : null}
                    {canResolve ? (
                        <Button size="small" type="link" icon={<CheckCircleOutlined />} onClick={() => onResolve(comment.id)}>
                            Resolve
                        </Button>
                    ) : null}
                </div>

                {isReplyOpen ? (
                    <form
                        className="portal-comments__reply"
                        onSubmit={async (e) => {
                            e.preventDefault();
                            await onPostReply(replyBody, comment.id);
                            setReplyBody('');
                            setReplyTo(null);
                        }}
                    >
                        <Input.TextArea
                            rows={2}
                            placeholder={`Reply to ${comment.author_display_name || 'comment'}…`}
                            value={replyBody}
                            onChange={(e) => setReplyBody(e.target.value)}
                            autoFocus
                        />
                        <div className="portal-comments__reply-actions">
                            <Button size="small" type="primary" htmlType="submit" disabled={!replyBody.trim()}>Reply</Button>
                        </div>
                    </form>
                ) : null}

                {comment.replies && comment.replies.length > 0 ? (
                    <ul className="portal-comments__replies">
                        {comment.replies.map((r) => (
                            <CommentItem {...props} key={r.id} comment={r} depth={depth + 1} />
                        ))}
                    </ul>
                ) : null}
            </div>
        </li>
    );
}

function threadComments(flat) {
    const byId = new Map(flat.map((c) => [c.id, {...c, replies: []}]));
    const roots = [];
    for (const c of byId.values()) {
        if (c.parent_id && byId.has(c.parent_id)) {
            byId.get(c.parent_id).replies.push(c);
        } else {
            roots.push(c);
        }
    }
    return roots;
}

function optimisticRow(id, body, parent_id, spec_path, json_pointer, user) {
    return {
        id,
        spec_path,
        json_pointer: json_pointer || null,
        body,
        parent_id: parent_id || null,
        source: 'human',
        anchor_diff: null,
        created_at: new Date().toISOString(),
        edited_at: null,
        resolved_at: null,
        resolved_by: null,
        user_id: user.id,
        author_display_name: user.display_name,
        author_avatar_url: user.avatar_url,
        author_email: user.email,
        _optimistic: true,
    };
}

function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString();
}

export default CommentsPanel;
